import { Controller, useWatch, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SERVER_SIZES } from './AsteriskConfigForm'

export interface ControllerConfigFormValues {
  controllerInstanceSize: string
  callEndpoint: string
  phoneNumberPrefix: string
  callTimeout: number
  playHello: boolean
  noStage1AMD: boolean
  noStage2AMD: boolean
  botEndpoint: string
  botExtension: string
  kickBotWhenTranferCompleted: boolean
  managerEndpoint: string
  managerPhoneNumberPrefix: string
  transferMode: string
}

// Props accept specific types but can be passed wider form types via type assertion
interface ControllerConfigFormProps {
  register: UseFormRegister<ControllerConfigFormValues>
  control: Control<ControllerConfigFormValues>
  errors: FieldErrors<ControllerConfigFormValues>
  disabled?: boolean
  instanceSizeDisabled?: boolean // Only disable instance size, not other fields
  availableEndpoints: string[]
}


export function ControllerConfigForm({
  register,
  control,
  errors,
  disabled = false,
  instanceSizeDisabled = false,
  availableEndpoints,
}: ControllerConfigFormProps) {
  const hasEndpoints = availableEndpoints.length > 0
  const transferMode = useWatch({ control, name: 'transferMode' })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Controller Configuration</CardTitle>
        <CardDescription>Configure the Controller instance settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Server Size */}
        <div className="space-y-2">
          <Label htmlFor="controllerInstanceSize">Server Size</Label>
          <Controller
            name="controllerInstanceSize"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={instanceSizeDisabled || disabled}
              >
                <SelectTrigger id="controllerInstanceSize">
                  <SelectValue placeholder="Select server size" />
                </SelectTrigger>
                <SelectContent>
                  {SERVER_SIZES.map((size) => (
                    <SelectItem key={size.value} value={size.value}>
                      {size.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {instanceSizeDisabled && (
            <p className="text-detail text-gray-500">
              Server size can only be changed when instance is down and no tasks are in progress
            </p>
          )}
        </div>

        {/* Call Configuration */}
        <div className="space-y-4">
          <h3 className="text-body font-semibold text-gray-700 border-b pb-2">Call Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="callEndpoint">Call Endpoint</Label>
              {hasEndpoints ? (
                <Controller
                  name="callEndpoint"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                      <SelectTrigger id="callEndpoint">
                        <SelectValue placeholder="Select endpoint" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableEndpoints.map((endpoint) => (
                          <SelectItem key={endpoint} value={endpoint}>
                            {endpoint}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              ) : (
                <Input
                  {...register('callEndpoint')}
                  id="callEndpoint"
                  type="text"
                  placeholder="No endpoints in pjsip.conf"
                  disabled={disabled}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="phoneNumberPrefix">Phone Number Prefix</Label>
              <Input
                {...register('phoneNumberPrefix')}
                id="phoneNumberPrefix"
                type="text"
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="callTimeout">Call Timeout (sec)</Label>
              <Input
                {...register('callTimeout', {
                  valueAsNumber: true,
                  min: { value: 10, message: 'Must be at least 10' },
                  max: { value: 180, message: 'Must be at most 180' },
                })}
                id="callTimeout"
                type="number"
                min="10"
                max="180"
                className={errors.callTimeout ? 'border-red-500' : ''}
                disabled={disabled}
              />
              {errors.callTimeout && (
                <p className="text-detail text-red-500">{errors.callTimeout.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Controller
              name="playHello"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="playHello"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              )}
            />
            <Label htmlFor="playHello" className="font-medium">
              Play Hello
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Controller
              name="noStage1AMD"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="noStage1AMD"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              )}
            />
            <Label htmlFor="noStage1AMD" className="font-medium">
              Disable first stage AMD
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Controller
              name="noStage2AMD"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="noStage2AMD"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              )}
            />
            <Label htmlFor="noStage2AMD" className="font-medium">
              Disable second stage AMD
            </Label>
          </div>
        </div>

        {/* Bot Configuration */}
        <div className="space-y-4">
          <h3 className="text-body font-semibold text-gray-700 border-b pb-2">Bot Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="botExtension">Bot Extension</Label>
              <Input
                {...register('botExtension')}
                id="botExtension"
                type="text"
                placeholder="e.g. default"
                disabled={disabled}
              />
              <p className="text-detail text-gray-500">
                SIP user part (before @endpoint). Leave empty to call endpoint directly.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="botEndpoint">Bot Endpoint</Label>
              {hasEndpoints ? (
                <Controller
                  name="botEndpoint"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                      <SelectTrigger id="botEndpoint">
                        <SelectValue placeholder="Select endpoint" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableEndpoints.map((endpoint) => (
                          <SelectItem key={endpoint} value={endpoint}>
                            {endpoint}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              ) : (
                <Input
                  {...register('botEndpoint')}
                  id="botEndpoint"
                  type="text"
                  placeholder="No endpoints in pjsip.conf"
                  disabled={disabled}
                />
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Controller
              name="kickBotWhenTranferCompleted"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="kickBotWhenTranferCompleted"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              )}
            />
            <Label htmlFor="kickBotWhenTranferCompleted" className="font-medium">
              Kick bot when transfer started
            </Label>
          </div>
        </div>

        {/* Manager Configuration */}
        <div className="space-y-4">
          <h3 className="text-body font-semibold text-gray-700 border-b pb-2">Manager Configuration</h3>

          {/* Transfer Mode */}
          <div className="space-y-2">
            <Label htmlFor="transferMode">Transfer Mode</Label>
            <Controller
              name="transferMode"
              control={control}
              render={({ field }) => (
                <Select value={field.value || 'conference'} onValueChange={field.onChange} disabled={disabled}>
                  <SelectTrigger id="transferMode">
                    <SelectValue placeholder="Select transfer mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conference">Conference</SelectItem>
                    <SelectItem value="refer">REFER</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {transferMode === 'refer' ? null : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="managerEndpoint">Manager Endpoint</Label>
                {hasEndpoints ? (
                  <Controller
                    name="managerEndpoint"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value || '_none'}
                        onValueChange={(val) => field.onChange(val === '_none' ? '' : val)}
                        disabled={disabled}
                      >
                        <SelectTrigger id="managerEndpoint">
                          <SelectValue placeholder="Select endpoint" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">None</SelectItem>
                          {availableEndpoints.map((endpoint) => (
                            <SelectItem key={endpoint} value={endpoint}>
                              {endpoint}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                ) : (
                  <Input
                    {...register('managerEndpoint')}
                    id="managerEndpoint"
                    type="text"
                    disabled={disabled}
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="managerPhoneNumberPrefix">Manager Phone Number Prefix</Label>
                <Input
                  {...register('managerPhoneNumberPrefix')}
                  id="managerPhoneNumberPrefix"
                  type="text"
                  disabled={disabled}
                />
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
